import dayjs, { type Dayjs } from "dayjs";

// PURE TASK RULES SHARED BY THE CALENDAR AND DAILY TASKS. PORTED FROM
// `src/app/_pages/task/task.service.ts`, `func.service.ts` AND `task.component.ts`.

export const TASK_ALERT_OPTIONS = [
  { value: "", name: "None", minutes: null },
  { value: "BEFORE_MINS_5", name: "5 Minutes before", minutes: 5 },
  { value: "BEFORE_MINS_15", name: "15 Minutes before", minutes: 15 },
  { value: "BEFORE_MINS_30", name: "30 Minutes before", minutes: 30 },
  { value: "BEFORE_MINS_60", name: "1 Hour before", minutes: 60 },
] as const;

export type TaskAlertOption = (typeof TASK_ALERT_OPTIONS)[number]["value"];

// `GetAlertTime`: THE ALERT FIRES N MINUTES BEFORE THE START; NO OPTION, NO ALERT
export function alertTime(option: string | null | undefined, start: Date): Date | null {
  const minutes = TASK_ALERT_OPTIONS.find((o) => o.value === option)?.minutes ?? null;
  return minutes === null ? null : dayjs(start).add(-minutes, "minute").toDate();
}

// `GetExpirationDate`: A TASK EXPIRES THREE HOURS AFTER IT ENDS
export function expirationDate(end: Date): Date {
  return dayjs(end).add(3, "hour").toDate();
}

// `RoundToNearestQuarterHour`: UNDER 8 MINUTES PAST A QUARTER ROUNDS DOWN, ELSE UP
export function roundToQuarterHour(d: Dayjs): Dayjs {
  const rest = d.minute() % 15;
  const moved = rest < 8 ? d.subtract(rest, "minute") : d.add(15 - rest, "minute");
  return moved.second(0).millisecond(0);
}

// EVENT COLORS (`TASK_COLORS`). COMPLETED WINS OVER MANAGER-CREATED.
export const TASK_COLORS = { default: "#009aff", completed: "#198754", managerCreated: "#6f42c1" } as const;

export function taskColor(task: { completed?: boolean; managerUserId?: number | null }): string {
  if (task.completed) return TASK_COLORS.completed;
  if (task.managerUserId) return TASK_COLORS.managerCreated;
  return TASK_COLORS.default;
}

// TASK ACTION CODE -> PLAYER COMMUNICATION TYPE. THE ACTION IS THE CHANNEL THE
// CONTACT WAS MADE ON; `OTHER` HAS NO EQUIVALENT AND IS DELIBERATELY NOT LOGGED.
export const TASK_ACTION_COMMUNICATION_TYPES: Readonly<Record<string, string>> = {
  PHONE: "Phone",
  TEXT: "SMS",
  EMAIL: "Email",
  "IN-PERSON": "InPerson",
};

// BODY FOR `POST /player/player-communication`, WHICH ONLY PERSISTS A RECORD - IT
// NEVER SENDS ANYTHING TO THE PLAYER (THAT IS `/communications`, NEVER CALLED
// HERE). `hostId` IS OMITTED: THE SERVER TAKES IT FROM THE TOKEN. RETURNS NULL
// WHEN THE ACTION HAS NO CHANNEL, WHICH MEANS "NOTHING TO LOG", NOT FAILURE.
export function taskContactLog(input: { playerId: number; taskId: number; actionCode: string | undefined; note: string | null | undefined }) {
  const communicationTypeName = input.actionCode ? TASK_ACTION_COMMUNICATION_TYPES[input.actionCode] : undefined;
  if (!communicationTypeName) return null;
  return {
    playerId: input.playerId,
    communicationTypeName,
    note: input.note || "",
    body: input.note || "",
    direction: "OUTBOUND",
    status: "SENT",
    metadata: JSON.stringify({ loggedOnly: true, source: "TASK", taskId: input.taskId }),
  };
}

// BUSINESS HOURS ARRIVE AS A JSON STRING ON THE USER (`User.businessHours`), OF
// WHICH ONLY `startTime`/`endTime` ("HH:mm") ARE USED. ANGULAR THROWS WHEN THE
// USER HAS NONE (THE SELECT AND SAVE HANDLERS DEREFERENCE IT); THE PILOT TREATS
// MISSING OR UNPARSEABLE HOURS AS "NO RESTRICTION" INSTEAD.
export interface BusinessHours {
  startTime: string;
  endTime: string;
  // PASSED THROUGH TO FULLCALENDAR ONLY, FOR ITS NON-BUSINESS SHADING - AS ANGULAR
  // DOES. THE PILOT'S OWN CHECKS IGNORE IT, LIKE ANGULAR'S. ABSENT, FULLCALENDAR
  // TREATS BUSINESS DAYS AS MONDAY TO FRIDAY.
  daysOfWeek?: number[];
}

const HHMM = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export function parseBusinessHours(raw: string | null | undefined): BusinessHours | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<BusinessHours> | null;
    if (v && typeof v.startTime === "string" && typeof v.endTime === "string" && HHMM.test(v.startTime) && HHMM.test(v.endTime)) {
      const days = Array.isArray(v.daysOfWeek) && v.daysOfWeek.every((d) => Number.isInteger(d) && d >= 0 && d <= 6) ? v.daysOfWeek : undefined;
      return { startTime: v.startTime, endTime: v.endTime, ...(days ? { daysOfWeek: days } : {}) };
    }
  } catch {
    // FALL THROUGH
  }
  return null;
}

const minutesOf = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number) as [number, number];
  return h * 60 + m;
};

// `isWithinBusinessHours`: THE WHOLE SLOT SITS INSIDE THE DAY'S HOURS. LIKE
// ANGULAR, `daysOfWeek` IS IGNORED - THE SAME HOURS APPLY EVERY DAY.
export function withinBusinessHours(hours: BusinessHours | null, start: Date, end: Date): boolean {
  if (!hours) return true;
  const s = dayjs(start);
  const e = dayjs(end);
  const sm = s.hour() * 60 + s.minute();
  // AN END OF MIDNIGHT THE NEXT DAY IS THE END OF THIS DAY
  const em = e.isSame(s, "day") ? e.hour() * 60 + e.minute() : 24 * 60;
  return sm >= minutesOf(hours.startTime) && em <= minutesOf(hours.endTime);
}

// THE THREE SAVE CHECKS, IN ANGULAR'S ORDER AND WITH ITS TOAST TEXT (`isDateInPast`,
// `isWithinBusinessHours`, THE END-TIME CHECK). THE PAST CHECK IS DAY-GRANULAR, AS
// THERE: EARLIER TODAY IS ALLOWED.
export function validateTaskTimes(start: Date, end: Date, hours: BusinessHours | null, now: Dayjs = dayjs()): string | null {
  if (dayjs(start).isBefore(now.startOf("day"))) return "The date and times selected are in the past, please try again.";
  if (!withinBusinessHours(hours, start, end)) return "The times selected are not within your business hours, please try again.";
  if (!dayjs(end).isAfter(start)) return "The end time must be after the start time, please try again.";
  return null;
}
